import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MegaMenu, MegaMenuMobile } from './MegaMenu';
import UserArea from './UserArea';
import SearchBar from './SearchBar';
import DirectMessaging from './messaging/DirectMessaging';
import './Navigation.css';

const Navigation = () => {
  const { currentUser, isAuthenticated, supabase } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDmConversations, setOpenDmConversations] = useState([]);
  const openDmConversationsRef = useRef([]);

  // Keep ref in sync with state
  useEffect(() => {
    openDmConversationsRef.current = openDmConversations;
  }, [openDmConversations]);

  // Auto-popup DMs when new messages arrive (if enabled)
  useEffect(() => {
    if (!isAuthenticated || !currentUser?.id || !currentUser?.dm_auto_popup || currentUser?.dm_enabled === false) {
      return;
    }

    const dmChannel = supabase
      .channel(`dm-auto-popup:${currentUser.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'dm_messages',
          filter: `recipient_id=eq.${currentUser.id}`,
        },
        async (payload) => {
          const senderId = payload.new.sender_id;
          const existingConversation = openDmConversationsRef.current.find(
            (conv) => conv.recipientId === senderId
          );
          if (existingConversation) return;

          try {
            const { data: senderProfile, error } = await supabase
              .from('profiles')
              .select('id, username, display_name')
              .eq('id', senderId)
              .single();
            if (error) throw error;
            setOpenDmConversations((prev) => [
              ...prev,
              {
                recipientId: senderProfile.id,
                recipientName: senderProfile.display_name || senderProfile.username,
              },
            ]);
          } catch (error) {
            console.error('[Navigation] Error auto-opening DM:', error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(dmChannel);
    };
  }, [isAuthenticated, currentUser?.id, currentUser?.dm_auto_popup, currentUser?.dm_enabled, supabase]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <nav className="main-nav" aria-label="Main navigation">
        {/* ── Left: Logo + Mega Menu ── */}
        <div className="nav-left">
          <div className="nav-logo">
            <Link to="/" onClick={closeMobileMenu}>
              <img
                src="/globe-logo.png"
                alt=""
                className="nav-logo__icon"
                aria-hidden="true"
              />
              <span className="nav-logo__text">The Orbital Ledger</span>
            </Link>
          </div>
          <MegaMenu onNavigate={closeMobileMenu} />
        </div>

        {/* ── Center: Search Bar ── */}
        <div className="nav-center">
          <SearchBar />
        </div>

        {/* ── Right: User Area ── */}
        <div className="nav-right">
          <UserArea
            onNavigate={closeMobileMenu}
            openDmConversations={openDmConversations}
            setOpenDmConversations={setOpenDmConversations}
          />
        </div>

        {/* ── Mobile toggle ── */}
        <button
          className="mobile-menu-toggle"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen((v) => !v)}
        >
          {isMobileMenuOpen ? '\u2715' : '\u2630'}
        </button>

        {/* ── Mobile menu ── */}
        {isMobileMenuOpen && (
          <div className="nav-mobile-menu">
            <MegaMenuMobile onNavigate={closeMobileMenu} />
            <UserArea
              mobile
              onNavigate={closeMobileMenu}
              openDmConversations={openDmConversations}
              setOpenDmConversations={setOpenDmConversations}
            />
          </div>
        )}
      </nav>

      {/* Direct Messaging Windows */}
      {openDmConversations.map((conversation, index) => (
        <div
          key={conversation.recipientId}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: `${20 + index * 360}px`,
            zIndex: 1000,
            width: '340px',
            maxHeight: '500px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          }}
        >
          <DirectMessaging
            recipientId={conversation.recipientId}
            recipientName={conversation.recipientName}
            onClose={() => {
              setOpenDmConversations((prev) =>
                prev.filter((conv) => conv.recipientId !== conversation.recipientId)
              );
            }}
          />
        </div>
      ))}
    </>
  );
};

export default Navigation;
